import React, {FC, ReactNode, useEffect, useState} from 'react';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {OkCancelDialog} from '../../../components/common/dialogs/okCancelDialog';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {
  AnalyticsCreateExportOptionsModel,
  AsyncExportTypesEnum,
} from '../../../models/api/analyticsExportModel';
import {makeEnumRuntimeType} from '../../../models/api/apiTypes';
import {InterfaceEventsEnum} from '../../../services/tracking';
import {updateAnalyticsExportsColumnsAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {getAnalyticsExportsColumns} from '../../../store/data/localPreferences/localPreferencesSelectors';
import {getAnalyticsDetailsSelectedMetricWithOptions} from '../../../store/panels/analytics/details/analyticsPanelDetailsSelectors';
import {
  createAnalyticsExportAsyncAction,
  fetchAnalyticsColumnNames,
  fetchAnalyticsEventTypes,
  fetchAnalyticsExports,
} from '../../../store/panels/analytics/exports/analyticsPanelExportsActions';
import {
  getAnalyticsActivitiesColumnNames,
  getAnalyticsActivitiesOrMessagesExportsList,
  getAnalyticsEventTypes,
  getAnalyticsMessagesColumnNames,
  getIsActivitiesAndMessagesColumnsAndEventTypesLoaded,
} from '../../../store/panels/analytics/exports/analyticsPanelExportsSelectors';
import {getAnalyticsFilters} from '../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {ReportFilterOptionsEnum} from '../../helpers/analyticsReportHelpers';
import {mapFiltersToTerms} from '../../helpers/analyticsSavedViewsHelpers';
import {useAnalyticsTracking} from '../../helpers/tracking/useAnalyticsTracking';
import {separatorConfigs} from './helpers/common';
import {ExportColumnsInput} from './helpers/exportColumnsInput';
import {ExportEventTypesInput} from './helpers/exportEventTypesInput';
import {ExportInfoText} from './helpers/exportInfoText';
import {ExportItemList} from './helpers/exportItemList';
import {ExportSeparatorInput} from './helpers/exportSeparatorInput';
import {ExportType, ExportTypeInput} from './helpers/exportTypeInput';

/*
 * Props.
 */

interface AnalyticsExportDialogProps {
  onRequestClose: Handler;
  isMetricDetails?: boolean;
  title?: ReactNode;
}

/*
 * Constants.
 */

const availableExportTypes: ReadonlyArray<ExportType> = [
  {
    value: AsyncExportTypesEnum.MESSAGES,
    title: <Trans id="analytics-exports-messages-export-title">Messages export</Trans>,
  },
  {
    value: AsyncExportTypesEnum.ACTIVITIES,
    title: <Trans id="analytics-exports-activities-export-title">Full events export</Trans>,
  },
];

const defaultTitle = <Trans id="analytics-export-dialog-title">Export data</Trans>;

/*
 * Component.
 */

export const AnalyticsExportDialog: FC<AnalyticsExportDialogProps> = ({
  onRequestClose,
  isMetricDetails,
  title,
}) => {
  const dispatch = useAppDispatch();
  const [selectedExportType, setSelectedExportType] = useState<ExportType>(availableExportTypes[0]);
  const [selectedSeparator, setSelectedSeparator] = useState(separatorConfigs[0]);
  const [selectedColumns, setSelectedColumns] = useState<ReadonlyArray<string>>([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState<ReadonlyArray<string>>([]);

  const [availableColumns, setAvailableColumns] = useState<ReadonlyArray<string>>([]);

  const isLoaded = useAppSelector(getIsActivitiesAndMessagesColumnsAndEventTypesLoaded);
  const exportsList = useAppSelector(getAnalyticsActivitiesOrMessagesExportsList);
  const filters = useAppSelector(getAnalyticsFilters);
  const trackAnalytics = useAnalyticsTracking();

  const availableMessageColumns = useAppSelector(getAnalyticsMessagesColumnNames);
  const availableActivitiesColumns = useAppSelector(getAnalyticsActivitiesColumnNames);
  const availableEventTypes = useAppSelector(getAnalyticsEventTypes);

  // For metrics details exports only.
  const {selectedMetric, options} = useAppSelector(getAnalyticsDetailsSelectedMetricWithOptions);

  // These are the columns previously selected by the user for both the messages and activities export type.
  // NOTE: This is a local preference, not one which comes from the API.
  const analyticsExportsColumnsPreference = useAppSelector(getAnalyticsExportsColumns);

  useInitialEffect(() => {
    if (!isLoaded) {
      dispatch(fetchAnalyticsColumnNames());
      dispatch(fetchAnalyticsEventTypes());
      dispatch(fetchAnalyticsExports());
    }
  });

  // When export types change, we have to reset the dialog dropdowns.
  useEffect(() => {
    switch (selectedExportType.value) {
      case AsyncExportTypesEnum.MESSAGES:
        setAvailableColumns(availableMessageColumns);
        setSelectedColumns(analyticsExportsColumnsPreference?.messagesColumns ?? availableMessageColumns);
        setSelectedEventTypes(availableEventTypes);
        break;
      case AsyncExportTypesEnum.ACTIVITIES:
        setAvailableColumns(availableActivitiesColumns);
        setSelectedColumns(
          analyticsExportsColumnsPreference?.activitiesColumns ?? availableActivitiesColumns,
        );
        setSelectedEventTypes(availableEventTypes);
        break;
      default:
        throw new Error(`This modal does not support this export type: ${selectedExportType.value}`);
    }
  }, [
    selectedExportType,
    availableMessageColumns,
    availableActivitiesColumns,
    availableEventTypes,
    analyticsExportsColumnsPreference?.activitiesColumns,
    analyticsExportsColumnsPreference?.messagesColumns,
  ]);

  const onAsyncExportRequestClick = () => {
    if (!makeEnumRuntimeType(AsyncExportTypesEnum).is(selectedExportType.value)) {
      return;
    }

    const exportOptions: AnalyticsCreateExportOptionsModel = {
      export_type: selectedExportType.value,
      sep: selectedSeparator.value,
      columns: selectedColumns,
      event_types: selectedEventTypes,
      filters: {terms: mapFiltersToTerms(filters)},
      metric_details: isMetricDetails
        ? {
            selected_metric: selectedMetric,
            options: options?.[ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS],
          }
        : undefined,
    };

    dispatch(createAnalyticsExportAsyncAction(exportOptions));
    dispatch(updateAnalyticsExportsColumnsAction(exportOptions.export_type, exportOptions.columns));

    trackAnalytics(InterfaceEventsEnum.ANALYTICS_VIEW_EXPORT, {
      export_type: exportOptions.export_type,
    });
  };

  return (
    <OkCancelDialog
      title={title || defaultTitle}
      okButtonLabel={<Trans id="analytics-export-request-button">Request export</Trans>}
      okButtonVariant={ButtonVariantsEnum.PRIMARY}
      onRequestClose={onRequestClose}
      onOkClick={onAsyncExportRequestClick}
      width={500}
    >
      <>
        <ExportTypeInput
          availableExportTypes={availableExportTypes}
          selectedExportType={selectedExportType}
          setSelectedExportType={setSelectedExportType}
          isLoading={!isLoaded}
        />
        <ExportSeparatorInput
          separatorConfigs={separatorConfigs}
          selectedSeparator={selectedSeparator}
          setSelectedSeparator={setSelectedSeparator}
          isLoading={!isLoaded}
        />
        <ExportColumnsInput
          availableColumns={availableColumns}
          selectedColumns={selectedColumns}
          setColumns={setSelectedColumns}
          isLoading={!isLoaded}
        />
        <ExportEventTypesInput
          availableEventTypes={availableEventTypes}
          selectedEventTypes={selectedEventTypes}
          setEventTypes={setSelectedEventTypes}
          isLoading={!isLoaded}
          isDisabled={selectedExportType.value === AsyncExportTypesEnum.MESSAGES}
        />
        <ExportInfoText
          isLoading={!isLoaded}
          text={
            <Trans id="analytics-export-info-text">
              Exports are CSV files with the raw data used in the analytics dashboard. Export links will
              expire after 30 days.
            </Trans>
          }
          subtext={<Trans id="analytics-export-info-subtext">View documentation</Trans>}
        />
        <ExportItemList exportItems={exportsList} isLoading={!isLoaded} />
      </>
    </OkCancelDialog>
  );
};
